import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Megaphone, Plus, Pin, Search, Trash2, Edit3 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import { pengumumanAPI } from "../lib/api";

export const PageHeader = ({ title, subtitle, icon: Icon, action }) => (
  <div className="flex items-start justify-between gap-4 mb-6">
    <div className="flex items-center gap-3">
      <div className="h-11 w-11 rounded-xl brand-gradient text-white grid place-items-center shadow-md"><Icon className="h-5 w-5" /></div>
      <div>
        <h1 className="text-xl md:text-2xl font-extrabold text-slate-800" style={{ fontFamily: "Poppins, sans-serif" }}>{title}</h1>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
    </div>
    {action}
  </div>
);

const Pengumuman = () => {
  const { user } = useApp();
  const canEdit = user.role !== "warga";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Info", body: "", pinned: false });

  const load = () => pengumumanAPI.list().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = items.filter((p) => p.title.toLowerCase().includes(q.toLowerCase()) || p.body.toLowerCase().includes(q.toLowerCase()));
  const pinned = filtered.filter((p) => p.pinned);
  const other = filtered.filter((p) => !p.pinned);

  const [editingId, setEditingId] = useState(null);

  const openNew = () => { setEditingId(null); setForm({ title: "", category: "Info", body: "", pinned: false, image: "", tanggal: "" }); setOpen(true); };
  const openEdit = (p) => { setEditingId(p.id); setForm({ title: p.title || "", category: p.category || "Info", body: p.body || "", pinned: !!p.pinned, image: p.image || "", tanggal: p.tanggal || p.date || "" }); setOpen(true); };

  const submit = async () => {
    if (!form.title || !form.body) { toast.error("Judul dan isi wajib diisi"); return; }
    try {
      if (editingId) { await pengumumanAPI.update(editingId, form); toast.success("Pengumuman diperbarui"); }
      else { await pengumumanAPI.create(form); toast.success("Pengumuman berhasil dipublikasikan"); }
      setForm({ title: "", category: "Info", body: "", pinned: false, image: "", tanggal: "" });
      setEditingId(null);
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const remove = async (id) => {
    try { await pengumumanAPI.remove(id); toast.success("Pengumuman dihapus"); load(); } catch { toast.error("Gagal menghapus"); }
  };

  return (
    <div>
      <PageHeader title="Pengumuman" subtitle="Informasi terbaru untuk seluruh warga" icon={Megaphone}
        action={canEdit && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
            <DialogTrigger asChild><Button className="brand-btn" onClick={openNew} data-testid="pengumuman-add-btn"><Plus className="h-4 w-4 mr-1" /> Buat Pengumuman</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editingId ? "Edit Pengumuman" : "Pengumuman Baru"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Judul</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>Kategori</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Info">Info</SelectItem>
                      <SelectItem value="Kegiatan">Kegiatan</SelectItem>
                      <SelectItem value="Keuangan">Keuangan</SelectItem>
                      <SelectItem value="Rapat">Rapat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Tanggal (kosongkan untuk hari ini)</Label><Input type="date" value={form.tanggal || ""} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></div>
                <div><Label>Isi</Label><Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
                <div><Label>URL Gambar (opsional)</Label><Input value={form.image || ""} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://... atau /api/files/..." /></div>
                {form.image && <img src={form.image} alt="" className="max-h-40 rounded-md border" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} /> Sematkan di atas</label>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={submit} className="brand-btn" data-testid="pengumuman-save-btn">{editingId ? "Simpan Perubahan" : "Publikasikan"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />
      <div className="relative mb-5 max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Cari pengumuman..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
      </div>
      {pinned.length > 0 && (
        <div className="mb-6">
          <div className="text-xs uppercase tracking-wider text-amber-600 font-bold mb-3 flex items-center gap-1"><Pin className="h-3.5 w-3.5" /> Disematkan</div>
          <div className="grid md:grid-cols-2 gap-4">
            {pinned.map((p) => (
              <Card key={p.id} className="card-hover border-amber-200 bg-amber-50/30">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-bold text-slate-800">{p.title}</h3>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-amber-500 hover:bg-amber-500 text-white">{p.category}</Badge>
                      {canEdit && <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-emerald-700" data-testid={`pengumuman-edit-${p.id}`}><Edit3 className="h-4 w-4" /></button>}
                      {canEdit && <button onClick={() => remove(p.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-3">{p.body}</p>
                  <div className="text-xs text-slate-400 mt-3">{p.author} • {p.date}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {other.map((p) => (
          <Card key={p.id} className="card-hover border-slate-100 overflow-hidden">
            {p.image && <img src={p.image} alt={p.title} className="w-full h-40 object-cover" />}
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{p.category}</Badge>
                <div className="flex gap-1">
                  {canEdit && <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-emerald-700"><Edit3 className="h-4 w-4" /></button>}
                  {canEdit && <button onClick={() => remove(p.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
              <h3 className="font-bold text-slate-800 mb-1">{p.title}</h3>
              <p className="text-sm text-slate-600 line-clamp-3">{p.body}</p>
              <div className="text-xs text-slate-400 mt-3">{p.author} • {p.date}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Pengumuman;
